// ============================================
// AI Training Mock Data
// ============================================

// Training Frameworks
export interface TrainingFramework {
  id: string;
  name: string;
  version: string;
  description: string;
  icon: 'Target' | 'HeartPulse' | 'AudioWaveform' | 'Sparkles';
}

export const TRAINING_FRAMEWORKS: TrainingFramework[] = [
  {
    id: 'nvidia-tao',
    name: 'NVIDIA TAO',
    version: '6.0',
    description: 'Transfer learning toolkit for computer vision',
    icon: 'Target',
  },
  {
    id: 'nvidia-monai',
    name: 'NVIDIA MONAI',
    version: '1.5.1',
    description: 'Medical AI framework for healthcare imaging',
    icon: 'HeartPulse',
  },
  {
    id: 'nvidia-nemo',
    name: 'NVIDIA NeMo',
    version: '2.0',
    description: 'End-to-end platform for speech and NLP AI',
    icon: 'AudioWaveform',
  },
  {
    id: 'float16-training',
    name: 'Float16 Training Framework',
    version: '1.0',
    description: 'Optimized training for enterprise AI workloads',
    icon: 'Sparkles',
  },
];

// Base Models for Fine-tuning
export interface BaseModel {
  id: string;
  name: string;
  parameters: string;
  description: string;
  vramRequired: number;
  compatibleFrameworks: string[];
}

export const BASE_MODELS_FOR_TRAINING: BaseModel[] = [
  // NVIDIA TAO models (Computer Vision)
  {
    id: 'resnet50',
    name: 'ResNet-50',
    parameters: '25M',
    description: 'Classic image classification backbone',
    vramRequired: 4,
    compatibleFrameworks: ['nvidia-tao'],
  },
  {
    id: 'efficientnet-b0',
    name: 'EfficientNet-B0',
    parameters: '5M',
    description: 'Efficient image classification model',
    vramRequired: 2,
    compatibleFrameworks: ['nvidia-tao'],
  },
  {
    id: 'yolov8',
    name: 'YOLOv8',
    parameters: '11M',
    description: 'Real-time object detection',
    vramRequired: 8,
    compatibleFrameworks: ['nvidia-tao'],
  },
  // NVIDIA MONAI models (Medical Imaging)
  {
    id: 'unet-medical',
    name: 'UNet (Medical)',
    parameters: '31M',
    description: 'Medical image segmentation',
    vramRequired: 8,
    compatibleFrameworks: ['nvidia-monai'],
  },
  {
    id: 'densenet121-medical',
    name: 'DenseNet-121',
    parameters: '8M',
    description: 'Medical image classification',
    vramRequired: 4,
    compatibleFrameworks: ['nvidia-monai'],
  },
  {
    id: 'swin-unetr',
    name: 'Swin UNETR',
    parameters: '62M',
    description: '3D medical image segmentation',
    vramRequired: 16,
    compatibleFrameworks: ['nvidia-monai'],
  },
  // NVIDIA NeMo models (Speech & NLP)
  {
    id: 'conformer-ctc',
    name: 'Conformer-CTC',
    parameters: '120M',
    description: 'Speech recognition model',
    vramRequired: 8,
    compatibleFrameworks: ['nvidia-nemo'],
  },
  {
    id: 'fastpitch',
    name: 'FastPitch',
    parameters: '45M',
    description: 'Text-to-speech synthesis',
    vramRequired: 4,
    compatibleFrameworks: ['nvidia-nemo'],
  },
  {
    id: 'megatron-gpt-1.3b',
    name: 'Megatron-GPT 1.3B',
    parameters: '1.3B',
    description: 'Large-scale language model',
    vramRequired: 24,
    compatibleFrameworks: ['nvidia-nemo'],
  },
  // Float16 Training Framework models (LLMs)
  {
    id: 'llama-3.1-8b',
    name: 'Llama 3.1 8B',
    parameters: '8B',
    description: 'Efficient for fine-tuning',
    vramRequired: 16,
    compatibleFrameworks: ['float16-training'],
  },
  {
    id: 'llama-3.1-70b',
    name: 'Llama 3.1 70B',
    parameters: '70B',
    description: 'Large model fine-tuning',
    vramRequired: 140,
    compatibleFrameworks: ['float16-training'],
  },
  {
    id: 'mistral-7b',
    name: 'Mistral 7B',
    parameters: '7B',
    description: 'Fast and efficient',
    vramRequired: 14,
    compatibleFrameworks: ['float16-training'],
  },
];

// Helper function to get models for a specific framework
export const getModelsForFramework = (frameworkId: string): BaseModel[] => {
  return BASE_MODELS_FOR_TRAINING.filter((model) =>
    model.compatibleFrameworks.includes(frameworkId)
  );
};

// Training Setup Info (result card)
export interface TrainingSetupInfo {
  framework: string;
  frameworkVersion: string;
  baseModel: string;
  setupCommand: string;
  notebookUrl: string;
}

export const mockTrainingSetupInfo = (
  taskId: string,
  frameworkId: string,
  modelId: string
): TrainingSetupInfo => {
  const framework = TRAINING_FRAMEWORKS.find((f) => f.id === frameworkId);
  const model = BASE_MODELS_FOR_TRAINING.find((m) => m.id === modelId);

  // Framework-specific setup commands
  const getSetupCommand = (fwId: string, mdId: string): string => {
    switch (fwId) {
      case 'nvidia-tao':
        return `# NVIDIA TAO Toolkit Setup
source /opt/nvidia-tao/bin/activate

# Configure TAO experiment
tao model ${mdId} train \\
  --gpus 1 \\
  --experiment_spec /workspace/specs/${mdId}_train.yaml \\
  --results_dir /workspace/results/${mdId}

# Export trained model
tao model ${mdId} export \\
  --model /workspace/results/${mdId}/weights/best.tlt \\
  --output /workspace/export/${mdId}.onnx`;
      case 'nvidia-monai':
        return `# NVIDIA MONAI Setup
source /opt/nvidia-monai/bin/activate

# Run MONAI training pipeline
python -m monai.bundle run training \\
  --meta_file /workspace/configs/${mdId}/metadata.json \\
  --config_file /workspace/configs/${mdId}/train.yaml \\
  --bundle_root /workspace/bundles/${mdId}

# Evaluate model
python -m monai.bundle run evaluating \\
  --config_file /workspace/configs/${mdId}/evaluate.yaml`;
      case 'nvidia-nemo':
        return `# NVIDIA NeMo Setup
source /opt/nvidia-nemo/bin/activate

# Download pretrained model
python -c "from nemo.collections.asr.models import EncDecCTCModel; \\
  EncDecCTCModel.from_pretrained('${mdId}')"

# Run NeMo training
python /opt/nvidia-nemo/examples/asr/speech_to_text.py \\
  --config-path=/workspace/configs/${mdId} \\
  --config-name=config.yaml \\
  trainer.devices=1 \\
  trainer.max_epochs=50 \\
  model.train_ds.manifest_filepath=/workspace/data/train_manifest.json \\
  model.validation_ds.manifest_filepath=/workspace/data/val_manifest.json

# Export to ONNX
python -m nemo.deploy.export \\
  --model /workspace/checkpoints/${mdId}.nemo \\
  --output /workspace/export/${mdId}.onnx`;
      case 'float16-training':
        return `# Float16 Training Framework Setup
source /opt/float16-training/bin/activate

# Initialize distributed training
float16-train init --model ${mdId} --num-gpus 1

# Start fine-tuning
float16-train finetune \\
  --model ${mdId} \\
  --dataset /workspace/data/train.jsonl \\
  --output /workspace/checkpoints/${mdId} \\
  --epochs 3 \\
  --batch-size 4 \\
  --learning-rate 2e-5

# Export to GGUF format
float16-train export --format gguf --quantize q4_k_m`;
      default:
        return `# Setup environment
source /opt/${fwId}/bin/activate

# Start training
python train.py --model ${mdId} --output ./checkpoints`;
    }
  };

  return {
    framework: framework?.name || frameworkId,
    frameworkVersion: framework?.version || '',
    baseModel: model?.name || modelId,
    setupCommand: getSetupCommand(frameworkId, modelId),
    notebookUrl: `http://localhost:8888/lab?token=demo-${taskId.slice(0, 8)}`,
  };
};
